import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { api } from '../lib/api'
import { getVacancyBackground } from '../lib/vacancyCategories'
import { getLocalizedField, useSiteLang } from '../lib/localizedContent'
import ResumeApplicationModal from '../components/ResumeApplicationModal'
import SEO from '../components/SEO'
import { buildCanonicalUrl } from '../lib/site'
import {
  ArrowLeft,
  Briefcase,
  DollarSign,
  MapPin,
  Send,
} from 'lucide-react'

interface Vacancy {
  id: string
  title: string
  titleEN: string
  titleRU: string
  category?: string
  categoryRU?: string
  categoryEN?: string
  description: string
  descriptionEN: string
  descriptionRU: string
  requirements: string
  requirementsEN: string
  requirementsRU: string
  responsibilities: string
  responsibilitiesEN: string
  responsibilitiesRU: string
  salary?: string
  salaryRU?: string
  salaryEN?: string
  location?: string
  locationRU?: string
  locationEN?: string
  isActive: boolean
}

const VacanciesView = () => {
  const { id } = useParams<{ id: string }>()
  const { t, i18n } = useTranslation()
  const [vacancy, setVacancy] = useState<Vacancy | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isApplicationOpen, setIsApplicationOpen] = useState(false)

  const lang = useSiteLang()

  useEffect(() => {
    if (!id) return

    const loadVacancy = async () => {
      try {
        const data = await api.vacancies.getById(id)
        if (!data.isActive) {
          setError('not_found')
          return
        }
        setVacancy(data)
      } catch (err) {
        console.error('Error loading vacancy:', err)
        setError('not_found')
      } finally {
        setLoading(false)
      }
    }

    loadVacancy()
  }, [id])

  if (loading) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error || !vacancy) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-6">{t('vacancys.notFound')}</p>
          <Link to="/vacancy" className="btn-primary inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('vacancys.back')}
          </Link>
        </div>
      </div>
    )
  }

  const background = getVacancyBackground(vacancy.categoryRU || vacancy.category)
  const title = getLocalizedField(vacancy, 'title', lang)
  const salary = getLocalizedField(vacancy, 'salary', lang)
  const category = getLocalizedField(vacancy, 'category', lang)
  const location = getLocalizedField(vacancy, 'location', lang)
  const description = getLocalizedField(vacancy, 'description', lang)
  const responsibilities = getLocalizedField(vacancy, 'responsibilities', lang)
  const requirements = getLocalizedField(vacancy, 'requirements', lang)

  return (
    <div className="pt-16">
      <SEO
        title={title}
        description={description.slice(0, 160)}
        canonical={buildCanonicalUrl(`/vacancy/${vacancy.id}`)}
        lang={i18n.resolvedLanguage ?? i18n.language}
      />
      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage: background.image
            ? `${background.gradient}, url(${background.image})`
            : background.gradient,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container-max relative z-10 section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/vacancy"
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('vacancys.back')}
            </Link>

            {category && (
              <p className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3">
                {category}
              </p>
            )}

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 max-w-4xl">
              {title}
            </h1>

            <div className="flex flex-wrap gap-4 text-white/90">
              {location && (
                <div className="flex items-center text-sm">
                  <MapPin className="w-4 h-4 mr-2" />
                  {location}
                </div>
              )}
              {salary && (
                <div className="flex items-center text-sm">
                  <DollarSign className="w-4 h-4 mr-2" />
                  {salary}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid gap-8 lg:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('vacancys.descriptionTitle')}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {description}
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('vacancys.responsibilities')}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {responsibilities}
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('vacancys.requirements')}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {requirements}
                </p>
              </div>

              <button
                  type="button"
                  onClick={() => setIsApplicationOpen(true)}
                  className="btn-primary flex items-center justify-center"
                >
                  {t('vacancys.apply')}
                  <Send className="w-4 h-4 ml-2" />
                </button>
            </motion.div>


          </div>
        </div>
      </section>

      <ResumeApplicationModal
        isOpen={isApplicationOpen}
        onClose={() => setIsApplicationOpen(false)}
        vacancyId={vacancy.id}
        vacancyTitle={title}
      />
    </div>
  )
}

export default VacanciesView
